<template>
  <Teleport to="body">
    <div class="foodleap-dd foodleap-dd-overlay" @click.self="close">
      <div class="foodleap-dd-dialog antialiased" role="dialog" aria-modal="true">
        <button class="foodleap-dd-close" @click="close" aria-label="Close">×</button>

        <!-- Unauthenticated -->
        <div v-if="state === 'unauthenticated'" class="text-center">
          <div class="w-20 h-20 rounded-full tw-flex items-center justify-center mx-auto mb-6" style="background-color: rgba(112,193,255,0.12);">
            <Icon name="lock" size="36" style="color: #70C1FF;"/>
          </div>
          <h1 class="text-3xl font-bold dd-text-slate mb-3">Sign in to download</h1>
          <p class="dd-text-slate-60 text-lg font-light mb-8">
            You need an account to access your Deep Dive Report.
          </p>
          <div class="tw-flex flex-col gap-3">
            <a
              @click="$router.push(loginUrl)"
              class="dd-btn-primary tw-inline-flex items-center justify-center bg-slate text-white rounded-full font-bold py-4 px-10 text-base no-underline"
            >
              Log in
            </a>
            <a
              @click="$router.push(registerUrl)"
              class="dd-btn-ghost-slate tw-inline-flex items-center justify-center rounded-full font-semibold py-4 px-10 text-base no-underline"
            >
              Create an account
            </a>
          </div>
        </div>

        <!-- Loading -->
        <div v-else-if="state === 'loading'" class="text-center">
          <div class="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
          <p class="dd-text-slate-60 text-lg">Preparing your download…</p>
        </div>

        <!-- Valid — download ready -->
        <div v-else-if="state === 'valid'" class="text-center">
          <div class="dd-success-icon w-20 h-20 rounded-full tw-flex items-center justify-center mx-auto mb-6 bg-green-50">
            <Icon name="check-circle" size="36"/>
          </div>
          <h1 class="text-3xl font-bold dd-text-slate mb-3">Your report is ready</h1>
          <p class="dd-text-slate-60 text-lg font-light mb-8">
            Click below to download the Deep Dive Report PowerPoint file.
          </p>
          <a
            :href="downloadUrl"
            target="_blank"
            rel="noopener"
            class="dd-btn-submit tw-inline-flex items-center justify-center gap-2 bg-slate text-white rounded-full font-bold py-4 px-10 text-base no-underline hover:opacity-90 transition-opacity"
          >
            <Icon name="download" size="20"/>
            Download Report
          </a>
          <p class="dd-text-slate-60 text-sm mt-6 font-light">
            This link can only be used once. The file will download as a <strong>.{{ fileExtension }}</strong> file.
          </p>
        </div>

        <!-- Already used -->
        <div v-else-if="state === 'already_used'" class="text-center">
          <div class="w-20 h-20 rounded-full tw-flex items-center justify-center mx-auto mb-6 bg-amber-50">
            <Icon name="warning" size="36"/>
          </div>
          <h1 class="text-3xl font-bold dd-text-slate mb-3">Link already used</h1>
          <p class="dd-text-slate-60 text-lg font-light mb-8">
            This download link has already been used. Each link can only be used once.
          </p>
          <a @click="close" class="dd-text-slate underline text-base font-semibold cursor-pointer">
            ← Back to the report page
          </a>
        </div>

        <!-- Invalid token -->
        <div v-else-if="state === 'invalid'" class="text-center">
          <div class="w-20 h-20 rounded-full tw-flex items-center justify-center mx-auto mb-6 bg-red-50">
            <Icon name="error" size="36"/>
          </div>
          <h1 class="text-3xl font-bold dd-text-slate mb-3">Invalid link</h1>
          <p class="dd-text-slate-60 text-lg font-light mb-8">
            This download link is invalid or has expired. Please request a new report from the Deep Dive page.
          </p>
          <a @click="close" class="dd-text-slate underline text-base font-semibold cursor-pointer">
            ← Request a new report
          </a>
        </div>

        <!-- Generic error -->
        <div v-else-if="state === 'error'" class="text-center">
          <div class="w-20 h-20 rounded-full tw-flex items-center justify-center mx-auto mb-6 bg-red-50">
            <Icon name="error" size="36"/>
          </div>
          <h1 class="text-3xl font-bold dd-text-slate mb-3">Something went wrong</h1>
          <p class="dd-text-slate-60 text-lg font-light mb-8">
            We couldn't process your download link. Please try again or contact us if the problem persists.
          </p>
          <button
            class="dd-btn-submit tw-inline-flex items-center justify-center bg-slate text-white rounded-full font-bold py-4 px-10 text-base"
            @click="resolveToken"
          >
            Try again
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script lang="ts">
  import { defineComponent } from 'vue'
  import Icon from '../Icon/Icon.vue'
  import { resolveDownloadToken } from '@/api/downloadable'

  type DownloadState = 'unauthenticated' | 'loading' | 'valid' | 'already_used' | 'invalid' | 'error'

  export default defineComponent({
    name: 'DeepDiveDownloadPage',
    components: { Icon },

    data() {
      return {
        state: 'loading' as DownloadState,
        downloadUrl: '',
        fileName: 'DataScouts_Deep_Dive_Report.pdf',
      }
    },

    computed: {
      token(): string {
        return this.$route.params.token as string
      },
      fileExtension(): string {
        const dot = this.fileName.lastIndexOf('.')
        return dot !== -1 ? this.fileName.slice(dot + 1).toUpperCase() : 'PDF'
      },
      isLoggedIn(): boolean {
        return !!this.$store.getters.isLoggedIn
      },
      loginUrl(): string {
        return `/login?redirect_uri=${encodeURIComponent(this.$route.fullPath)}`
      },
      registerUrl(): string {
        return `/register?redirect_uri=${encodeURIComponent(this.$route.fullPath)}`
      },
    },

    mounted() {
      if (!this.isLoggedIn) {
        this.state = 'unauthenticated'
        return
      }
      this.resolveToken()
    },

    methods: {
      close() {
        this.$router.push('/deep-dive-report')
      },

      async resolveToken() {
        this.state = 'loading'

        try {
          const result = await resolveDownloadToken(this.token)
          this.downloadUrl = result.download_url
          this.fileName = result.file_name
          this.state = 'valid'
        } catch (err: any) {
          if (err?.statusCode === 410 || err?.error === 'already_used') {
            this.state = 'already_used'
          } else if (err?.statusCode === 404 || err?.error === 'invalid_token') {
            this.state = 'invalid'
          } else {
            this.state = 'error'
          }
        }
      },
    },
  })
</script>

<style lang="scss" scoped>
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

  .foodleap-dd-overlay {
    position: fixed;
    inset: 0;
    z-index: 1000;
    background-color: rgba(33, 36, 74, 0.6);
    backdrop-filter: blur(2px);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
  }

  .foodleap-dd-dialog {
    position: relative;
    background: #ffffff;
    border-radius: 1.5rem;
    padding: 3rem;
    width: 100%;
    max-width: 28rem;
    box-shadow: 0 24px 64px rgba(33, 36, 74, 0.18);
  }

  .foodleap-dd-close {
    position: absolute;
    top: 1.25rem;
    right: 1.25rem;
    width: 2rem;
    height: 2rem;
    border-radius: 50%;
    border: none;
    background: rgba(33, 36, 74, 0.08);
    color: rgba(33, 36, 74, 0.5);
    font-size: 1.25rem;
    line-height: 1;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.2s, color 0.2s;

    &:hover {
      background: rgba(33, 36, 74, 0.14);
      color: #21244A;
    }
  }

  .foodleap-dd {
    font-family: 'Inter', sans-serif;
    color: #21244A;

    $ps: #21244A;
    $pg: #5CCC61;

    .dd-text-slate {
      color: $ps;
    }

    .dd-text-slate-60 {
      color: rgba($ps, 0.6);
    }

    .bg-slate {
      background-color: $ps;
    }

    .dd-success-icon {
      background-color: rgba($pg, 0.12);
      color: $pg;
    }

    .dd-btn-submit,
    .dd-btn-primary {
      cursor: pointer;
      transition: opacity 0.2s ease;

      &:hover {
        opacity: 0.88;
      }
    }

    .dd-btn-ghost-slate {
      border: 1px solid rgba($ps, 0.2);
      color: rgba($ps, 0.7);
      cursor: pointer;
      transition: border-color 0.2s, color 0.2s;

      &:hover {
        border-color: rgba($ps, 0.4);
        color: $ps;
      }
    }
  }
</style>
